import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { HiOutlineExternalLink, HiOutlineBriefcase, HiOutlineAcademicCap, HiLightningBolt, HiBadgeCheck } from 'react-icons/hi';

import { AppWrap, MotionWrap } from '../../wrapper';
import { client } from '../../client';
import './Skills.scss';

const getCompanyUrl = (work) => {
  const rawUrl = work?.companyUrl || work?.company_url;
  if (!rawUrl) return null;
  if (/^https?:\/\//i.test(rawUrl)) return rawUrl;
  return `https://${rawUrl}`;
};

const Skills = () => {
  const [experiences, setExperiences] = useState([]);
  const [education, setEducation] = useState([]);
  const [certifications, setCertifications] = useState([]);

  // Core skills list
  const coreSkills = ['Oracle APEX', 'PL/SQL', 'React', 'JavaScript', 'Node.js', 'Python', 'Data Modeling', 'Git', 'Agile'];

  useEffect(() => {
    const workQuery = '*[_type == "workExperience"] | order(order asc)';
    const eduQuery = '*[_type == "education"] | order(order asc, endYear desc)';
    const certQuery = '*[_type == "certifications"] | order(year desc)';

    client.fetch(workQuery).then((data) => {
      setExperiences(data);
    });

    client.fetch(eduQuery).then((data) => {
      setEducation(data);
    });

    client.fetch(certQuery).then((data) => {
      setCertifications(data);
    });
  }, []);

  return (
    <div className="experience">
      {/* Header */}
      <motion.div
        className="experience__header"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <h2 className="experience__title">
          My <span className="gradient-text">Experience</span>
        </h2>
      </motion.div>

      {/* Main Content - Two Columns */}
      <div className="experience__content">
        
        {/* Left Column - Work Experience */}
        <motion.div
          className="experience__column"
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          viewport={{ once: true }}
        >
          <div className="experience__section-header">
            <HiOutlineBriefcase className="experience__section-icon" />
            <h3>Work</h3>
          </div>

          <div className="experience__timeline">
            {experiences.map((work, index) => (
              <motion.div 
                key={work._id || index}
                className="experience__job"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="experience__job-header">
                  <h4 className="experience__job-title">{work.name}</h4>
                  <span className="experience__job-date">
                    {work.startDate}{work.endDate ? ` - ${work.endDate}` : ''}
                  </span>
                </div>
                {getCompanyUrl(work) ? (
                  <a 
                    href={getCompanyUrl(work)} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="experience__job-company experience__job-company-link"
                  >
                    {work.company}
                    <HiOutlineExternalLink className="experience__job-company-icon" />
                  </a>
                ) : (
                  <p className="experience__job-company">{work.company}</p>
                )}
                {work.desc && (
                  <p className="experience__job-desc">{work.desc}</p>
                )}
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Right Column - Education, Skills & Certs */}
        <motion.div
          className="experience__column"
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          viewport={{ once: true }}
        >
          {/* Education */}
          <div className="experience__education-section">
            <div className="experience__section-header">
              <HiOutlineAcademicCap className="experience__section-icon" />
              <h3>Education</h3>
            </div>
            <div className="experience__education">
              {education.length > 0 ? (
                education.map((edu, index) => (
                  <motion.div
                    key={edu._id || index}
                    className="experience__edu-item"
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    viewport={{ once: true }}
                  >
                    <div className="experience__edu-header">
                      <h5 className="experience__edu-degree">{edu.degree}</h5>
                      <span className="experience__edu-years">
                        {edu.startYear} – {edu.endYear}
                      </span>
                    </div>
                    <p className="experience__edu-field">{edu.field}</p>
                    <p className="experience__edu-school">{edu.school}</p>
                    {edu.description && (
                      <p className="experience__edu-desc">{edu.description}</p>
                    )}
                  </motion.div>
                ))
              ) : (
                <p className="experience__empty-note">Add education in Sanity Studio</p>
              )}
            </div>
          </div>

          {/* Skills */}
          <div className="experience__skills-section">
            <div className="experience__section-header">
              <HiLightningBolt className="experience__section-icon" />
              <h3>Skills</h3>
            </div>
            <div className="experience__skills">
              {coreSkills.map((skill, index) => (
                <motion.span
                  key={skill}
                  className="experience__skill-tag"
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.2, delay: index * 0.03 }}
                  viewport={{ once: true }}
                >
                  {skill}
                </motion.span>
              ))}
            </div>
          </div>

          {/* Certifications */}
          <div className="experience__certs-section">
            <div className="experience__section-header">
              <HiBadgeCheck className="experience__section-icon" />
              <h3>Certifications</h3>
            </div>
            <div className="experience__certs">
              {certifications.slice(0, 4).map((cert, index) => (
                <motion.a
                  key={cert.title}
                  href={cert.certLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="experience__cert"
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  viewport={{ once: true }}
                >
                  <div className="experience__cert-info">
                    <h5>{cert.title}</h5>
                    <span className="experience__cert-year">{cert.year}</span>
                  </div>
                  <HiOutlineExternalLink className="experience__cert-link-icon" />
                </motion.a>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AppWrap(
  MotionWrap(Skills, 'app__skills'),
  'experience',
  'app__whitebg'
);
